import React from 'react';
import { Pill } from '@/components/ui/Pill';
import { LazySection } from '@/components/ui/LazySection';
import { ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

interface PlatformActiveFiltersProps {
  selectedCountries: string[];
  setSelectedCountries: (v: string[]) => void;
  selectedProviders: string[];
  setSelectedProviders: (v: string[]) => void;
  selectedTechnologies: string[];
  setSelectedTechnologies: (v: string[]) => void;
  selectedSamples: string[];
  setSelectedSamples: (v: string[]) => void;
  selectedProducts: string[];
  setSelectedProducts: (v: string[]) => void;
  selectedReadouts: string[];
  setSelectedReadouts: (v: string[]) => void;
  selectedPlexTypes: string[];
  setSelectedPlexTypes: (v: string[]) => void;
  selectedChemistry: string[];
  setSelectedChemistry: (v: string[]) => void;
  selectedResolution: string[];
  setSelectedResolution: (v: string[]) => void;
  selectedThroughput: string[];
  setSelectedThroughput: (v: string[]) => void;
  selectedSlideResolution: string[];
  setSelectedSlideResolution: (v: string[]) => void;
  selectedApplication: string[];
  setSelectedApplication: (v: string[]) => void;
  technologyOptions: string[];
  productOptions: { id: string; name: string }[];
  onClearFilters: () => void;
  viewMode: 'card' | 'list';
  setViewMode: (v: 'card' | 'list') => void;
  sortBy: string;
  setSortBy: (v: string) => void;
}

export default function PlatformActiveFilters(props: PlatformActiveFiltersProps) {
  const anyActive = [
    props.selectedCountries.length,
    props.selectedProviders.length,
    props.selectedTechnologies.length,
    props.selectedSamples.length,
    props.selectedProducts.length,
    props.selectedReadouts.length,
    props.selectedPlexTypes.length,
    props.selectedChemistry.length,
    props.selectedResolution.length,
    props.selectedThroughput.length,
    props.selectedSlideResolution.length,
    props.selectedApplication.length,
  ].some(Boolean);

  // Sort options
  const sortOptions = [
    { value: "name-asc", label: "Sort by Name (A-Z)" },
    { value: "name-desc", label: "Sort by Name (Z-A)" },
    { value: "provider-asc", label: "Sort by Provider (A-Z)" },
    { value: "provider-desc", label: "Sort by Provider (Z-A)" },
    { value: "location-asc", label: "Sort by Location (A-Z)" },
    { value: "location-desc", label: "Sort by Location (Z-A)" },
  ];

  const [sortOpen, setSortOpen] = React.useState(false);

  const currentSortLabel = sortOptions.find(opt => opt.value === props.sortBy)?.label.replace("Sort by", "Sorted by") || "Sort by...";
  
  return (
    <LazySection minHeight={40}>
      {/* Filter Pills */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {props.selectedCountries.map((country) => (
          <Pill key={country} type="selectedFilterCountry">
            {country}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove country filter" onClick={() => props.setSelectedCountries(props.selectedCountries.filter((c) => c !== country))}>×</button>
          </Pill>
        ))}
        {props.selectedProviders.map((provider) => (
          <Pill key={provider} type="selectedFilterProvider">
            {provider}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove provider filter" onClick={() => props.setSelectedProviders(props.selectedProviders.filter((p) => p !== provider))}>×</button>
          </Pill>
        ))}
        {props.selectedTechnologies.map((tech) => (
          <Pill key={tech} type="selectedFilterTechnology">
            {tech}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove technology filter" onClick={() => props.setSelectedTechnologies(props.selectedTechnologies.filter((t) => t !== tech))}>×</button>
          </Pill>
        ))}
        {props.selectedSamples.map((sample) => (
          <Pill key={sample} type="selectedFilterSample">
            {sample}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove sample filter" onClick={() => props.setSelectedSamples(props.selectedSamples.filter((s) => s !== sample))}>×</button>
          </Pill>
        ))}
        {props.selectedProducts.map((product) => (
          <Pill key={product} type="selectedFilterProduct">
            {props.productOptions.find(opt => opt.id === product)?.name || product}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove product filter" onClick={() => props.setSelectedProducts(props.selectedProducts.filter((p) => p !== product))}>×</button>
          </Pill>
        ))}
        {props.selectedReadouts.map((readout) => (
          <Pill key={readout} type="selectedFilterReadout">
            {readout}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove readout filter" onClick={() => props.setSelectedReadouts(props.selectedReadouts.filter((r) => r !== readout))}>×</button>
          </Pill>
        ))}
        {props.selectedPlexTypes.map((plex) => (
          <Pill key={plex} type="selectedFilterPlexType">
            {plex}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove plex type filter" onClick={() => props.setSelectedPlexTypes(props.selectedPlexTypes.filter((p) => p !== plex))}>×</button>
          </Pill>
        ))}
        {props.selectedChemistry.map((chem) => (
          <Pill key={chem} type="selectedFilterChemistry">
            {chem}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove chemistry filter" onClick={() => props.setSelectedChemistry(props.selectedChemistry.filter((c) => c !== chem))}>×</button>
          </Pill>
        ))}
        {props.selectedResolution.map((res) => (
          <Pill key={res} type="selectedFilterResolution">
            {res}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove resolution filter" onClick={() => props.setSelectedResolution(props.selectedResolution.filter((r) => r !== res))}>×</button>
          </Pill>
        ))}
        {props.selectedThroughput.map((thru) => (
          <Pill key={thru} type="selectedFilterThroughput">
            {thru}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove throughput filter" onClick={() => props.setSelectedThroughput(props.selectedThroughput.filter((t) => t !== thru))}>×</button>
          </Pill>
        ))}
        {props.selectedSlideResolution.map((sr) => (
          <Pill key={sr} type="selectedFilterSlideResolution">
            {sr}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove slide resolution filter" onClick={() => props.setSelectedSlideResolution(props.selectedSlideResolution.filter((s) => s !== sr))}>×</button>
          </Pill>
        ))}
        {props.selectedApplication.map((app) => (
          <Pill key={app} type="selectedFilterApplication">
            {app}
            <button type="button" className="ml-2 text-neutral-700 hover:text-neutral-900 font-bold focus:outline-none" aria-label="Remove application filter" onClick={() => props.setSelectedApplication(props.selectedApplication.filter((a) => a !== app))}>×</button>
          </Pill>
        ))}
      </div>
      
      {/* Sort and View Controls */}
      <div className="flex items-center justify-between w-full mb-3">
        {/* Left side - Sort Filter */}
        <Popover open={sortOpen} onOpenChange={setSortOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={sortOpen}
              className={cn(
                "w-64 h-10 justify-between border-0 font-sans text-base transition-colors bg-neutral-500 text-white hover:bg-neutral-600 focus:bg-neutral-600 [&_svg]:text-white"
              )}
            >
              <span className="truncate text-left">{currentSortLabel}</span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0 bg-white rounded-lg shadow-lg border border-neutral-200 font-sans text-neutral-900 z-50">
            <Command>
              <CommandGroup>
                {sortOptions.filter(opt => opt.value !== props.sortBy).map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={opt.value}
                    onSelect={() => {
                      props.setSortBy(opt.value);
                      setSortOpen(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:!bg-neutral-200 transition-colors data-[selected=true]:!bg-neutral-200"
                  >
                    <span className="flex-1">{opt.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </Command>
          </PopoverContent>
        </Popover>
        
        {/* Right side - Clear Filters and View Toggles */}
        <div className="flex items-center gap-2">
          {anyActive && (
            <button type="button" className="bg-neutral-200 text-neutral-800 rounded-md px-4 py-2 text-sm font-sans font-medium hover:bg-neutral-300 transition-colors whitespace-nowrap" onClick={props.onClearFilters}>Clear Filters</button>
          )}
          <button type="button" aria-label="List View" aria-pressed={props.viewMode === 'list'} onClick={() => props.setViewMode('list')} className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors border-none focus:outline-none ${props.viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'bg-neutral-100 text-neutral-500'}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect y="6" width="24" height="2" rx="1" fill="currentColor" /><rect y="11" width="24" height="2" rx="1" fill="currentColor" /><rect y="16" width="24" height="2" rx="1" fill="currentColor" /></svg>
          </button>
          <button type="button" aria-label="Card/Grid View" aria-pressed={props.viewMode === 'card'} onClick={() => props.setViewMode('card')} className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors border-none focus:outline-none ${props.viewMode === 'card' ? 'bg-primary-100 text-primary-600' : 'bg-neutral-100 text-neutral-500'}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="7" height="7" rx="2" fill="currentColor" /><rect x="14" y="3" width="7" height="7" rx="2" fill="currentColor" /><rect x="3" y="14" width="7" height="7" rx="2" fill="currentColor" /><rect x="14" y="14" width="7" height="7" rx="2" fill="currentColor" /></svg>
          </button>
        </div>
      </div>
    </LazySection>
  );
} 